import {
  AssociatedVM,
  EnvironmentRequest,
  ProductNode,
  ProductReference,
  ProductReferenceMapping,
  ProductReferenceType,
  RequestId,
  RequestState,
  UpdateDataSourcePayload,
  VerticalScaleUpPayload,
  VidmData,
  VidmProductStatus,
  VidmStatusType,
  VropsCollectorGroupsPayload
} from 'vrlcm-gui-types';
import {
  extractVidmHostProperties,
  Http,
  isRequestInprogress,
  safeJsonParse
} from 'vrlcm-gui-utils';
import { RequestApi } from '../request';
import { EnvironmentApi } from './Environments';
import { LcopsApiUri } from './uri';

export class ProductApi {
  public static async triggerProductInventorySync(
    environmentId: string,
    productId: string,
    tenantId?: string
  ) {
    const response = await Http.post<RequestId>(
      LcopsApiUri.Product.TRIGGER_PRODUCT_INVENTORY_SYNC_URL(
        environmentId,
        productId,
        tenantId
      )
    );
    return response.data.requestId;
  }

  public static getVidmData(tenantName?: string): Promise<VidmData> {
    return new Promise(async (resolve, reject) => {
      try {
        const globalEnvironment = await EnvironmentApi.getEnvironmentDataById(
          'globalenvironment'
        );
        const globalEnvironmentData =
          globalEnvironment &&
          safeJsonParse<EnvironmentRequest>(globalEnvironment.environmentData);
        if (
          globalEnvironment.status === RequestState.COMPLETED &&
          globalEnvironmentData
        ) {
          const {
            vidmHostname,
            vidmAdminPassword,
            vidmAdminUser,
            vidmVersion,
            vidmRootPassword,
            vidmSshPassword,
            vidmOAuthServiceClientSecret,
            vidmOAuthServiceClientId,
            tenancyEnabled,
            vidmProductCertificate,
            clusteredVidm,
            vidmDomainName,
            baseTenantHostname
          } = extractVidmHostProperties(globalEnvironmentData, tenantName);

          if (
            vidmHostname &&
            vidmAdminUser &&
            vidmAdminPassword &&
            vidmVersion
          ) {
            resolve({
              vidmDomainName,
              baseTenantHostname,
              vidmAdminPassword,
              vidmHost: vidmHostname,
              vidmAdminUser,
              vidmVersion,
              vidmRootPassword,
              vidmSshPassword,
              vidmOAuthServiceClientId,
              vidmOAuthServiceClientSecret,
              tenancyEnabled,
              vidmProductCertificate,
              clusteredVidm
            });
          } else {
            reject(VidmStatusType.vIDMHostNameUnknown);
          }
        } else if (
          globalEnvironment &&
          isRequestInprogress(globalEnvironment.status)
        ) {
          reject(VidmStatusType.InstallInprogress);
        } else {
          reject(VidmStatusType.NotInstalled);
        }
      } catch (e) {
        reject(VidmStatusType.NotInstalled);
      }
    });
  }

  public static async getVidmStatus(): Promise<VidmProductStatus> {
    let status, version;
    try {
      const vidmData = await ProductApi.getVidmData();
      status = VidmStatusType.Installed;
      version = vidmData.vidmVersion;
    } catch (e) {
      status = e;
    }
    try {
      const inprogressRequests = await RequestApi.getEnvironmentActiveRequests(
        'globalenvironment'
      );
      return { status, version, inprogressRequests };
    } catch (e) {
      return { status, version, inprogressRequests: [] };
    }
  }

  public static async getDependentProducts(
    environmentId: string,
    productId: string
  ) {
    const response = await Http.get<ProductReference[]>(
      LcopsApiUri.Product.GET_DEPENDENT_PRODUCTS_URL(environmentId, productId)
    );
    return response.data;
  }

  public static async getDependentProductsByEnvironment(
    version: string,
    productId: string
  ) {
    const response = await Http.get<any[]>(
      LcopsApiUri.Product.GET_ALL_ENVIRONMENTS_BY_PRODUCT(version, productId)
    );
    return response.data;
  }

  public static async getProductDependencies(
    environmentId: string,
    productId: string
  ) {
    const response = await Http.get<ProductReference[]>(
      LcopsApiUri.Product.GET_PRODUCT_DEPENDENCIES_URL(environmentId, productId)
    );
    return response.data;
  }

  public static async getDependentProductsByEnv(environmentId: string) {
    const response = await Http.get<ProductReference[]>(
      LcopsApiUri.Product.GET_DEPENDENT_PRODUCTS_BY_ENV_URL(environmentId)
    );
    return response.data;
  }

  public static async getProductDependenciesByEnv(environmentId: string) {
    const response = await Http.get<ProductReference[]>(
      LcopsApiUri.Product.GET_PRODUCT_DEPENDENCIES_BY_ENV_URL(environmentId)
    );
    return response.data;
  }

  public static async getProductReferenceMapping(
    environmentId: string,
    productId: string
  ) {
    const refType = ProductReferenceType.VIDM_AUTH_PROVIDER;
    const response = await Http.get<ProductReferenceMapping>(
      LcopsApiUri.Product.GET_PRODUCT_REFERENCE_MAPPING_URL(
        refType,
        environmentId,
        productId
      )
    );
    return response.data;
  }

  public static async validateDataSourcesPassword(
    environmentId: string,
    productId = 'vrni',
    requestPayload: UpdateDataSourcePayload
  ) {
    const response = await Http.post<RequestId>(
      LcopsApiUri.ProductV2.VALIDATE_DATA_SOURCES_URL(environmentId, productId),
      requestPayload
    );
    return response.data.requestId;
  }

  public static async updateDataSources(
    environmentId: string,
    productId = 'vrni',
    requestPayload: UpdateDataSourcePayload
  ) {
    const response = await Http.put<RequestId>(
      LcopsApiUri.ProductV2.DATA_SOURCES_URL(environmentId, productId),
      requestPayload
    );
    return response.data.requestId;
  }

  public static async getDataSourceAssociatations(
    environmentId: string,
    productId = 'vrni',
    dsIp: string
  ) {
    const response = await Http.get<ProductNode[]>(
      LcopsApiUri.ProductV2.GET_DATA_SOURCE_ASSOCIATIONS_URL(
        environmentId,
        productId,
        dsIp
      )
    );
    return response.data;
  }

  public static async deleteDataSource(
    environmentId: string,
    productId = 'vrni',
    payload: ProductNode
  ) {
    const response = await Http.delete<RequestId>(
      LcopsApiUri.ProductV2.DATA_SOURCES_URL(environmentId, productId),
      { data: payload }
    );
    return response.data.requestId;
  }

  public static async triggerVerticalScaleUp(
    environmentId: string,
    productId: string,
    payload: VerticalScaleUpPayload
  ) {
    const response = await Http.post<RequestId>(
      LcopsApiUri.ProductV2.VERTICAL_SCALE_UP_URL(environmentId, productId),
      payload
    );
    return response.data.requestId;
  }

  public static async validateVerticalScaleUp(
    environmentId: string,
    productId: string,
    payload: VerticalScaleUpPayload
  ) {
    const response = await Http.post<RequestId>(
      LcopsApiUri.ProductV2.VALIDATE_VERTICAL_SCALE_UP_URL(
        environmentId,
        productId
      ),
      payload
    );
    return response.data.requestId;
  }

  public static async getProductDeployedVms(
    environmentId: string,
    productId: string,
    getWindowsVms = false,
    tenantId?: string
  ) {
    const response = await Http.get<AssociatedVM[]>(
      LcopsApiUri.ProductV2.GET_PRODUCT_DEPLOYED_VMS_URL(
        environmentId,
        productId,
        getWindowsVms,
        tenantId
      )
    );
    return response.data;
  }

  public static async updateCollectorGroup(
    environmentId: string,
    productId: string,
    payload: VropsCollectorGroupsPayload
  ) {
    const response = await Http.post(
      LcopsApiUri.ProductV2.UPDATE_COLLECTOR_GROUP(environmentId, productId),
      payload
    );
    return response.data.requestId;
  }

  public static async replaceLicenseForProduct(
    environmentId: string,
    productId: string,
    licenseVmId: string,
    tenantId?: string,
    deleteLicenseVmIdList?: string[]
  ) {
    const body = { licenseVmId, deleteLicenseVmIdList };

    const response = await Http.post<RequestId>(
      LcopsApiUri.Product.PRODUCT_REPLACE_LICENSE_URL(
        environmentId,
        productId,
        tenantId
      ),
      body
    );
    return response.data;
  }

  public static async toggleFipsMode(
    environmentId: string,
    productId: string,
    fipsMode: string
  ) {
    const response = await Http.post<{
      requestId: string;
    }>(LcopsApiUri.ProductV2.TOGGLE_FIPS_MODE(environmentId, productId), {
      fipsMode
    });
    return response.data;
  }

  public static async updateNTPConfiguration(
    environmentId: string,
    productId: string,
    ntpServers: string,
    timeSyncMode: string
  ) {
    const response = await Http.post<{ requestId: string }>(
      LcopsApiUri.ProductV2.UPDATE_NTP_CONFIGURATION(environmentId, productId),
      {
        ntpServers,
        timeSyncMode,
        preValidate: false
      }
    );
    return response.data;
  }

  public static async updateNTPConfigurationPrecheck(
    environmentId: string,
    productId: string,
    ntpServers: string,
    timeSyncMode: string
  ) {
    const response = await Http.post<{ requestId: string }>(
      LcopsApiUri.ProductV2.UPDATE_NTP_CONFIGURATION_PRECHECK(
        environmentId,
        productId
      ),
      {
        ntpServers,
        timeSyncMode,
        preValidate: true
      }
    );
    return response.data;
  }

  public static async togglePendoSetting(
    environmentId: string,
    productId: string,
    pendoSettingValue: string
  ) {
    const response = await Http.post<{ requestId: string }>(
      LcopsApiUri.ProductV2.TOGGLE_PENDO_SETTING(environmentId, productId),
      {
        pendoSetting: pendoSettingValue
      }
    );
    return response.data;
  }

  public static async installManagementPack(
    environmentId: string,
    productId: string,
    adapterType: string,
    fileName?: string,
    vROpsEnvironmentId?: string
  ) {
    const payload: {
      adapterType: string;
      fileName?: string;
      vROpsEnvironmentId?: string;
    } = {
      adapterType
    };
    if (adapterType === 'installmp') {
      payload.fileName = fileName;
    }
    if (productId === 'vidm') {
      payload.vROpsEnvironmentId = vROpsEnvironmentId;
    }
    const response = await Http.post(
      LcopsApiUri.ProductV2.INSTALL_MANAGEMENT_PACK(environmentId, productId),
      payload
    );
    return response.data;
  }

  public static async vrliLogForwardingConfiguration(
    environmentId: string,
    productId: string,
    vropsLogEntities?: string[]
  ) {
    const response = await Http.post(
      LcopsApiUri.ProductV2.VRLI_LOG_FORWARDING_CONFIGURATION(
        environmentId,
        productId,
        vropsLogEntities?.join(',')
      )
    );
    return response.data;
  }

  public static async getEnvironmentsWithVrops() {
    const response = await EnvironmentApi.getAllEnvironmentsWithState(
      RequestState.COMPLETED
    );
    return response.filter(environmentItem => {
      return environmentItem.products.some(product => product.id === 'vrops');
    });
  }
}
